// src/components/slider/MainSlider.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    IconButton,
    Typography,
    Button,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

const MainSlider = ({ sliders }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [currentSlide, setCurrentSlide] = useState(0);
    const [displaySliders, setDisplaySliders] = useState([]);

    useEffect(() => {
        // If there's only one slide, duplicate it to maintain consistent styling
        if (sliders.length === 1) {
            setDisplaySliders([...sliders, {...sliders[0], id: 'duplicate'}]);
        } else {
            setDisplaySliders(sliders);
        }
    }, [sliders]);

    useEffect(() => {
        if (displaySliders.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentSlide(prev => (prev === displaySliders.length - 1 ? 0 : prev + 1));
        }, 5000);

        return () => clearInterval(interval);
    }, [displaySliders.length]);

    const nextSlide = () => {
        if (displaySliders.length <= 1) return;
        setCurrentSlide(prev => (prev === displaySliders.length - 1 ? 0 : prev + 1));
    };

    const prevSlide = () => {
        if (displaySliders.length <= 1) return;
        setCurrentSlide(prev => (prev === 0 ? displaySliders.length - 1 : prev - 1));
    };

    if (sliders.length === 0) return null;

    return (
        <Box sx={{
            maxWidth: `${window.screen.width < 1200 ? '90%' : '1200px'}`,
            margin: '40px auto auto auto',
            padding: isMobile ? '0 16px' : '0',
            width:'100%',
            fontFamily: '"Poppins", sans-serif'
        }}>
            <Box sx={{
                position: 'relative',
                height: isMobile ? 'auto' : '300px',
                width:'100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                borderRadius: '16px',
                overflow: 'hidden',
                boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                backgroundColor: '#fff'
            }}>
                {/* Navigation arrows - only show if more than one slide */}
                {displaySliders.length > 1 && (
                    <>
                        <IconButton
                            onClick={prevSlide}
                            sx={{
                                position: 'absolute',
                                left: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 20,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                color: '#2C3E50',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                        >
                            <KeyboardArrowLeft fontSize="large" />
                        </IconButton>

                        <IconButton
                            onClick={nextSlide}
                            sx={{
                                position: 'absolute',
                                right: 10,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                zIndex: 20,
                                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                                color: '#2C3E50',
                                '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.9)' }
                            }}
                        >
                            <KeyboardArrowRight fontSize="large" />
                        </IconButton>
                    </>
                )}

                {/* Slides container */}
                <Box sx={{
                    display: 'flex',
                    transition: 'transform 0.5s ease',
                    transform: `translateX(-${currentSlide * 100}%)`,
                    height: '100%',
                    width: '100%',
                }}>
                    {displaySliders.map((slide, index) => (
                        <Box
                            key={index}
                            sx={{
                                flex: '0 0 100%', // Ensure each slide takes full width
                                height: isMobile ? '500px' : '100%',
                                display: 'flex',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                overflow: 'hidden',
                                width:'100%'
                            }}
                        >
                            {/* Image container (left) */}
                            <Box sx={{
                                width: isMobile ? '100%' : '50%',
                                height: isMobile ? '50%' : '100%',
                                backgroundImage: `url(${slide.image})`,
                                backgroundSize: isMobile ? 'cover' : 'contain',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                position: 'relative',
                                '&:before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    zIndex: 1
                                }
                            }} />

                            {/* Content container (right) */}
                            <Box sx={{
                                width: isMobile ? '100%' : '50%',
                                height: isMobile ? '50%' : '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                alignItems: 'flex-start',
                                padding: isMobile ? '20px' : '40px',
                                backgroundColor: '#1f6848',
                                color: 'white',
                                boxSizing: 'border-box',
                                position: 'relative'
                            }}>
                                <Typography variant="h3" sx={{
                                    fontWeight: 700,
                                    fontSize: isMobile ? '1.8rem' : '2.5rem',
                                    mb: 1,
                                    lineHeight: 1.2
                                }}>
                                    {slide.title}
                                </Typography>

                                <Typography variant="body1" sx={{
                                    fontSize: isMobile ? '1rem' : '1.1rem',
                                    mb: 3,
                                    maxWidth: '500px'
                                }}>
                                    {slide.description}
                                </Typography>

                                <Button
                                    variant="contained"
                                    onClick={() => window.open(slide.link, '_blank')}
                                    sx={{
                                        backgroundColor: 'white',
                                        color: '#979797',
                                        fontWeight: 700,
                                        fontSize: isMobile ? '0.9rem' : '1rem',
                                        padding: '10px 30px',
                                        borderRadius: '30px',
                                        boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.2)',
                                        '&:hover': {
                                            backgroundColor: '#f5f5f5',
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                >
                                    {slide.button}
                                </Button>
                            </Box>
                        </Box>
                    ))}
                </Box>

                {/* Slider indicators - only show if more than one slide */}
                {displaySliders.length > 1 && (
                    <Box sx={{
                        position: 'absolute',
                        bottom: '20px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: '10px',
                        zIndex: 20
                    }}>
                        {sliders.map((_, index) => (
                            <Button
                                key={index}
                                onClick={() => setCurrentSlide(index)}
                                sx={{
                                    minWidth: '12px',
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    padding: 0,
                                    backgroundColor: currentSlide === index ? 'white' : 'rgba(255, 255, 255, 0.5)',
                                    '&:hover': {
                                        backgroundColor: currentSlide === index ? 'white' : 'rgba(255, 255, 255, 0.7)'
                                    }
                                }}
                            />
                        ))}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default MainSlider;